import Vue from 'vue'
import Vuex from 'vuex'
import VuexORM from '@vuex-orm/core'

// models
import User from '@/store/models/User'
import Organization from '@/store/models/Organization'
import Role from '@/store/models/Role'
import VideoRoomMaster from '@/store/models/VideoRoomMaster'
import VideoRoom from '@/store/models/VideoRoom'
import UserNotification from '@/store/models/UserNotification'
import GroupChat from '@/store/models/GroupChat'
import GroupChatPost from '@/store/models/GroupChatPost'
import PrivateChat from '@/store/models/PrivateChat'
import PrivateChatPost from '@/store/models/PrivateChatPost'
import Reaction from '@/store/models/Reaction'
import LicensedPlan from '@/store/models/LicensedPlan'
import Project from '@/store/models/Project'
import ProjectAttachment from '@/store/models/ProjectAttachment'
import TaskList from '@/store/models/TaskList'
import TaskCard from '@/store/models/TaskCard'
import TaskCardHistory from '@/store/models/TaskCardHistory'
import SystemNotification from '@/store/models/SystemNotification'

// managed models
import Account from '@/store/models/admin/Account'
import Bill from '@/store/models/admin/Bill'
import InviteUser from '@/store/models/admin/InviteUser'
import LicensePlanMaster from '@/store/models/admin/LicensePlanMaster'
import ManagedUser from '@/store/models/admin/ManagedUser'
import ManagedVideoRoomMaster from '@/store/models/admin/ManagedVideoRoomMaster'
import PaymentSystem from '@/store/models/admin/PaymentSystem'
import PaymentSystemMaster from '@/store/models/admin/PaymentSystemMaster'
import RoomPlanMaster from '@/store/models/admin/RoomPlanMaster'
import SeatPlanMaster from '@/store/models/admin/SeatPlanMaster'

// modules
import auth from '@/store/modules/auth'
import inviteAuth from '@/store/modules/inviteAuth'
import issues from '@/store/modules/issues'
import organizations from '@/store/modules/organizations'
import roles from '@/store/modules/roles'
import users from '@/store/modules/users'
import videoRoomMasters from '@/store/modules/videoRoomMasters'
import videoRooms from '@/store/modules/videoRooms'
import videoRoomMembers from '@/store/modules/videoRoomMembers'
import userNotifications from '@/store/modules/userNotifications'
import groupChats from '@/store/modules/groupChats'
import groupChatPosts from '@/store/modules/groupChatPosts'
import groupChatPostReactions from '@/store/modules/groupChatPostReactions'
import privateChats from '@/store/modules/privateChats'
import privateChatPosts from '@/store/modules/privateChatPosts'
import privateChatPostReactions from '@/store/modules/privateChatPostReactions'
import reactions from '@/store/modules/reactions'
import licensedPlans from '@/store/modules/licensedPlans'
import projects from '@/store/modules/projects'
import projectAttachments from '@/store/modules/projectAttachments'
import projectChatPosts from '@/store/modules/projectChatPosts'
import taskLists from '@/store/modules/taskLists'
import taskCards from '@/store/modules/taskCards'
import taskCardHistories from '@/store/modules/taskCardHistories'
import systemManage from '@/store/modules/systemManage'
import systemNotifications from '@/store/modules/systemNotifications'
import userGroupChatStatus from '@/store/modules/userGroupChatStatus'
import userPrivateChatStatus from '@/store/modules/userPrivateChatStatus'
import userOrganization from '@/store/modules/userOrganization'
import channels from '@/store/modules/channels'
import roomPeers from '@/store/modules/room/peers'
import roomChatMessages from '@/store/modules/room/chatMessages'
import roomWhiteboard from '@/store/modules/room/whiteboard'
import linkPreview from '@/store/modules/linkPreview'

// managed modules
import accounts from '@/store/modules/admin/accounts'
import bills from '@/store/modules/admin/bills'
import coupons from '@/store/modules/admin/coupons'
import inviteUsers from '@/store/modules/admin/inviteUsers'
import licensePlans from '@/store/modules/admin/licensePlans'
import licensePlanMasters from '@/store/modules/admin/licensePlanMasters'
import managedOrganization from '@/store/modules/admin/managedOrganization'
import managedUsers from '@/store/modules/admin/managedUsers'
import managedUserOrganization from '@/store/modules/admin/managedUserOrganization'
import managedVideoRoomMasters from '@/store/modules/admin/managedVideoRoomMasters'
import paymentSystems from '@/store/modules/admin/paymentSystems'
import paymentSystemMasters from '@/store/modules/admin/paymentSystemMasters'
import roomPlanMasters from '@/store/modules/admin/roomPlanMasters'
import seatPlanMasters from '@/store/modules/admin/seatPlanMasters'

Vue.use(Vuex)

const database = new VuexORM.Database()
database.register(User, users)
database.register(Organization, organizations)
database.register(Role, roles)
database.register(VideoRoomMaster, videoRoomMasters)
database.register(VideoRoom, videoRooms)
database.register(UserNotification, userNotifications)
database.register(GroupChat, groupChats)
database.register(GroupChatPost, groupChatPosts)
database.register(PrivateChat, privateChats)
database.register(PrivateChatPost, privateChatPosts)
database.register(Reaction, reactions)
database.register(LicensedPlan, licensedPlans)
database.register(Project, projects)
database.register(ProjectAttachment, projectAttachments)
database.register(TaskList, taskLists)
database.register(TaskCard, taskCards)
database.register(TaskCardHistory, taskCardHistories)
database.register(SystemNotification, systemNotifications)
database.register(Account, accounts)
database.register(Bill, bills)
database.register(InviteUser, inviteUsers)
database.register(LicensePlanMaster, licensePlanMasters)
database.register(ManagedUser, managedUsers)
database.register(ManagedVideoRoomMaster, managedVideoRoomMasters)
database.register(PaymentSystem, paymentSystems)
database.register(PaymentSystemMaster, paymentSystemMasters)
database.register(RoomPlanMaster, roomPlanMasters)
database.register(SeatPlanMaster, seatPlanMasters)

export default new Vuex.Store({
  state: {
    httpRequesting: false,
    httpSuccess: false,
    httpError: false,
    forbidden: false,
    notFound: false,
    internalServerError: false,
    unusable: false,
    menu: null,
    memberModalId: null,
    selectOrganizationModal: false,
    setupDefault: false
  },
  getters: {
    httpRequesting: state => state.httpRequesting,
    httpSuccess: state => state.httpSuccess,
    httpError: state => state.httpError,
    forbidden: state => state.forbidden,
    notFound: state => state.notFound,
    internalServerError: state => state.internalServerError,
    unusable: state => state.unusable,
    menu: state => state.menu,
    memberModalId: state => state.memberModalId,
    selectOrganizationModal: state => state.selectOrganizationModal,
    setupDefault: state => state.setupDefault
  },
  mutations: {
    setHttpRequesting(state, payload) {
      state.httpRequesting = Boolean(payload)
    },
    setHttpSuccess(state, payload) {
      state.httpSuccess = payload
    },
    setHttpError(state, payload) {
      state.httpError = payload
    },
    setForbidden(state, payload) {
      state.forbidden = payload
    },
    setNotFound(state, payload) {
      state.notFound = payload
    },
    setInternalServerError(state, payload) {
      state.internalServerError = payload
    },
    setUnusable(state, payload) {
      state.unusable = payload
    },
    setMenu(state, payload) {
      state.menu = payload
    },
    setMemberModalId(state, payload) {
      state.memberModalId = payload
    },
    setSelectOrganizationModal(state, payload) {
      state.selectOrganizationModal = payload
    },
    setupDefault(state, payload) {
      state.setupDefault = payload
    }
  },
  actions: {
    async setupDefaultDispatch({ dispatch, commit }) {
      dispatch('entities/roles/index')
      dispatch('entities/reactions/index')
      dispatch('channels/initCable')
      await dispatch('entities/users/index')
      commit('setupDefault', true)
    },
    changeMenu({ commit }, payload) {
      commit('setMenu', payload)
    },
    clearHttpStatus({ commit }) {
      commit('setHttpSuccess', false)
      commit('setHttpError', false)
    },
    clearComponentStatus({ commit }) {
      commit('setForbidden', false)
      commit('setNotFound', false)
      commit('setInternalServerError', false)
    },
    httpRequest({ commit }) {
      commit('setHttpSuccess', false)
      commit('setHttpError', false)
      commit('setHttpRequesting', true)
    },
    httpResponse({ commit }) {
      commit('setHttpRequesting', false)
    },
    resetAllModulesAndEntities({ commit, dispatch }) {
      dispatch('channels/clearCable')
      dispatch('entities/deleteAll')
      commit('auth/resetModule')
      commit('inviteAuth/resetModule')
      commit('projectChatPosts/resetModule')
      commit('entities/groupChatPosts/resetModule')
      commit('entities/privateChatPosts/resetModule')
      commit('entities/systemNotifications/resetModule')
      commit('entities/taskCardHistories/resetModule')
    }
  },
  plugins: [ VuexORM.install(database) ],
  modules: {
    auth,
    inviteAuth,
    issues,
    userGroupChatStatus,
    userPrivateChatStatus,
    userOrganization,
    groupChatPostReactions,
    privateChatPostReactions,
    projectChatPosts,
    videoRoomMembers,
    managedOrganization,
    managedUserOrganization,
    licensePlans,
    systemManage,
    channels,
    roomPeers,
    roomChatMessages,
    roomWhiteboard,
    linkPreview,
    coupons
  }
})
